import * as React from 'react';
import { Res } from 'core/api';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { PaymentDirection, Transaction, TransactionType, assertAreExternalTransactions, deviceDescription } from './ExternalPaymentsService.Common';
import { PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { ActionButtonDefinition } from 'core/ui';
import { Modal } from 'core/ui-next/modal';

export class RetryUndoPaymentScreen extends PosScreenClassComponent<{
	type: TransactionType,
	transaction: Transaction,
	direction: PaymentDirection
} & Modal.Actions<void>, { showIgnoreButton: boolean }> {

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	createInitialState(_props: React.ComponentProps<typeof RetryUndoPaymentScreen>): Nullable<{ showIgnoreButton: boolean; }> {
		return { showIgnoreButton: false };
	}

	protected getScreenId(): string {
		return 'RETRY_UNDO_PAYMENT';
	}

	protected getTitle(): string {
		return Res.Common.WARNING();
	}

	protected getImage() {
		return Images.Warning;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [null, null, null, null, null, null];
	}

	protected useKeypad(): boolean {
		return false;
	}

	protected useKeyboard(): boolean {
		return false;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	private getTexts(): { problemDescription: string, retryButtonText: string, consequencesText: string } {
		const { type, direction, transaction } = this.props;

		if (type === 'NoDocument' && direction === 'PAY') {
			return {
				problemDescription: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_NO_DOCUMENT_PAY_PROBLEM_DESCRIPTION(Math.abs(transaction.amount), deviceDescription(transaction.device)),
				retryButtonText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_NO_DOCUMENT_PAY_RETRY_BTN(),
				consequencesText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_NO_DOCUMENT_PAY_IGNORE_CONSEQUENCES()
			};
		}

		if (type === 'NoDocument' && direction === 'REFUND') {
			return {
				problemDescription: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_NO_DOCUMENT_REFUND_PROBLEM_DESCRIPTION(Math.abs(transaction.amount), deviceDescription(transaction.device)),
				retryButtonText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_NO_DOCUMENT_REFUND_RETRY_BTN(),
				consequencesText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_NO_DOCUMENT_REFUND_IGNORE_CONSEQUENCES()
			};
		}

		if (direction === 'PAY') {
			return {
				problemDescription: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_PAY_PROBLEM_DESCRIPTION(Math.abs(transaction.amount), deviceDescription(transaction.device)),
				retryButtonText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_PAY_RETRY_BTN(),
				consequencesText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_PAY_IGNORE_CONSEQUENCES()
			};
		}

		if (direction === 'REFUND') {
			return {
				problemDescription: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_REFUND_PROBLEM_DESCRIPTION(Math.abs(transaction.amount), deviceDescription(transaction.device)),
				retryButtonText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_REFUND_RETRY_BTN(),
				consequencesText: Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_REFUND_IGNORE_CONSEQUENCES()
			};
		}

		throw new Error('No se debería poder alcanzar este punto');
	}

	protected getContent(): React.ReactNode {

		const { problemDescription, retryButtonText, consequencesText } = this.getTexts();

		const ignoreButtonText = Res.Web.EXTERNAL_PAYMENT_RETRY_UNDO_IGNORE_BTN();

		return <div className='p-m'>
			<p className='alert alert-danger m-b-xl m-t-md text-16' style={{ display: 'flex', alignItems: 'baseline' }}>
				<span className='fa fa-exclamation-circle m-r-sm'></span>{problemDescription}
			</p>

			<div className='m-t-xl m-b-xl'>
				<UI.Button buttonStyle='primary' className='btn-block btn-lg' style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}} onClick={() => this.close()}>
					<Images.Retry className='m-r-sm' />
					<span>{retryButtonText}</span>
				</UI.Button>
			</div>

			{this.state.showIgnoreButton === false
				// Primero muestro un enlace...
				? <div className='text-center'>
					<a className='link' onClick={() => this.setState({ showIgnoreButton: true })}>
						<span className='fa fa-chevron-circle-right m-r-xs text-danger-2'></span>
						{ignoreButtonText}
					</a>
				</div>

				// ...y si lo pulsan, muestro un aviso y un botón
				: <div>
					<p className='font-bold text-16 text-center m-b-md'>{consequencesText}</p>

					<UI.Button buttonStyle='danger2' outline className='btn-block btn-lg' style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}} onClick={() => this.cancel()}>
						<Images.Warning className='m-r-sm' />
						<span>{ignoreButtonText}</span>
					</UI.Button>
				</div>}
		</div>;
	}

	static async retry(type: TransactionType, direction: PaymentDirection, transaction: Transaction): Promise<{ nextAction: 'retry-again' | 'ignore-failure' }> {
		assertAreExternalTransactions(transaction);
		const result = await Modal.show(RetryUndoPaymentScreen, { type, direction, transaction });
		return { nextAction: result.ok ? 'retry-again' : 'ignore-failure' };
	}
}
